import { useId } from "react"

import type { Annotation } from "@/types"
import {
  Badge,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"

import { cn } from "@/utils/cn"

import { OtherReviewerLabels } from "./OtherReviewerLabels"

interface CategoricalHumanFeedbackButtonProps {
  annotation: Annotation | null
  onValueChange: (
    floatValue: number | null,
    stringValue: string | null,
    setValue: string[] | null,
  ) => void
  onExplanationChange: (explanation: string | null) => void
  otherUserAnnotations?: Annotation[]
  categories: string[]
  categoryColors?: Record<string, string>
}

export const CategoricalHumanFeedbackButton = ({
  annotation,
  onValueChange,
  onExplanationChange: _onExplanationChange,
  otherUserAnnotations,
  categories,
  categoryColors,
}: CategoricalHumanFeedbackButtonProps) => {
  const triggerId = useId()
  const currentValue = annotation?.ground_truth_string_value ?? null

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={triggerId} id={`${triggerId}-label`}>
        Human Review
      </Label>
      <div className="flex items-start gap-1.5">
        <Select
          value={currentValue ?? ""}
          onValueChange={(val) => {
            if (val === currentValue) {
              onValueChange(null, null, null)
            } else {
              onValueChange(null, val, null)
            }
          }}
        >
          <SelectTrigger
            id={triggerId}
            aria-labelledby={`${triggerId}-label`}
            variant="secondary"
            className={cn(
              "transition-all duration-200",
              currentValue
                ? cn("font-medium", categoryColors?.[currentValue])
                : "text-coval-text-muted",
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <SelectValue placeholder="Select category" />
          </SelectTrigger>
          <SelectContent aria-label="Select category">
            {categories.map((category) => (
              <SelectItem key={category} value={category}>
                {category}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <OtherReviewerLabels
          annotations={otherUserAnnotations}
          renderValue={(otherAnnotation) =>
            otherAnnotation.ground_truth_string_value ? (
              <Badge>{otherAnnotation.ground_truth_string_value}</Badge>
            ) : (
              <span className="text-sm font-medium text-coval-text-muted">
                N/A
              </span>
            )
          }
        />
      </div>
    </div>
  )
}
